// Expression cycling utilities for avatar customization (updated for shape-only system)

/**
 * Simplified for the new shape-only system - cycles directly through available shapes
 * without size variants. Works with the new baseline + transform approach.
 */

import {
  FacialFeature,
  getAvailableShapeNames,
} from '@airhead-games/shared/configs';

/**
 * Get the next shape in cycling order for a given feature
 *
 * @param currentShape - Current shape name (e.g., 'arch', 'bean')
 * @param direction - Cycling direction
 * @param feature - Feature type ('eye' or 'mouth')
 * @returns Next shape name in sequence
 */
export function getNextShape(
  currentShape: string,
  direction: 'next' | 'prev',
  feature: FacialFeature
): string {
  const availableShapes = getAvailableShapeNames(feature);

  if (availableShapes.length <= 1) {
    return currentShape; // No cycling possible with 0-1 shapes
  }

  const currentIndex = availableShapes.indexOf(currentShape);

  // If current shape not found, default to first shape
  if (currentIndex === -1) {
    return availableShapes[0];
  }

  let nextIndex: number;

  if (direction === 'next') {
    nextIndex = (currentIndex + 1) % availableShapes.length;
  } else {
    nextIndex =
      (currentIndex - 1 + availableShapes.length) % availableShapes.length;
  }

  return availableShapes[nextIndex];
}

/**
 * Legacy compatibility: Get next expression for old system callers
 *
 * @param currentExpression - Current expression (now just shape name)
 * @param direction - Cycling direction
 * @param feature - Feature type ('eye' or 'mouth')
 * @returns Next expression (shape name)
 *
 * @deprecated Use getNextShape directly instead
 */
export function getNextExpression(
  currentExpression: string,
  direction: 'next' | 'prev',
  feature?: FacialFeature
): string {
  // Try to infer feature from expression if not provided
  const inferredFeature: FacialFeature =
    feature || (currentExpression.includes('eye') ? 'eye' : 'mouth');

  // In the new system, expressions are just shape names
  return getNextShape(currentExpression, direction, inferredFeature);
}

/**
 * Get all available shapes for a feature
 *
 * @param feature - Feature type ('eye' or 'mouth')
 * @returns Array of available shape names
 */
export function getAvailableShapes(feature: FacialFeature): string[] {
  return getAvailableShapeNames(feature);
}

/**
 * Check if a shape exists for a feature
 *
 * @param shapeName - Shape name to check
 * @param feature - Feature type
 * @returns True if shape exists for feature
 */
export function isValidShape(
  shapeName: string,
  feature: FacialFeature
): boolean {
  return getAvailableShapeNames(feature).includes(shapeName);
}

/**
 * Get a random shape for a feature
 *
 * @param feature - Feature type
 * @returns Random shape name
 */
export function getRandomShape(feature: FacialFeature): string {
  const shapes = getAvailableShapeNames(feature);
  return shapes[Math.floor(Math.random() * shapes.length)];
}
